/**
 * Maps customer dashboard routes to Control Tower feature IDs (aligned with internal Customer Feature Monitor catalog).
 */
const ROUTE_TO_FEATURE: Record<string, string> = {
  '': 'super-dashboard',
  'overview/super-dashboard': 'super-dashboard',
  'overview/dashboard': 'dashboard',
  'overview/dashboard-builder': 'dashboard-builder',
  'onboarding/overview': 'onboarding-overview',
  'onboarding/add-project': 'add-project',
  'onboarding/add-infrastructure': 'add-infrastructure',
  'onboarding/add-monitoring-sources': 'add-monitoring',
  'onboarding/validation-status': 'validation',
  'onboarding/setup-guide': 'setup-guide',
  'monitoring/app-performance': 'app-performance',
  'monitoring/errors': 'error-tracking',
  'monitoring/logs': 'logs-analytics',
  'monitoring/user-sessions': 'user-sessions',
  'monitoring/analytics': 'logs-analytics',
  'monitoring/alerts': 'alerts-notifications',
  'server-mgmt/servers': 'server-dashboard',
  'server-mgmt/networks': 'network-monitoring',
  'server-mgmt/databases': 'database-performance',
  'server-mgmt/containers': 'container-orchestration',
  'server-mgmt/services': 'service-health',
  'server-mgmt/patch-management': 'patch-management',
  'server-mgmt/fine-tuning': 'fine-tuning',
  'server-mgmt/cron-scripts': 'cron-scripts',
  'ai/auto-healing': 'self-healing',
  'ai/predictive-analytics': 'predictive-analytics',
  'ai/smart-alerts': 'ai-smart-alerts',
  'ai/insights': 'ai-insights',
  'ai/assistant': 'ai-assistant',
  'ai/self-healing': 'self-healing',
  'ai/spike-analysis': 'spike-analysis',
  'microservices/apis': 'microservices-overview',
  'microservices/websites': 'microservices-overview',
  'microservices/overview': 'microservices-overview',
  'microservices': 'microservices-fleet',
  'microservices/add': 'add-microservice',
  'microservices/service-mesh': 'service-mesh',
  'microservices/distributed-tracing': 'distributed-tracing',
  'microservices/agent-scripts': 'agent-scripts',
  'microservices/projects': 'microservices-projects',
  'microservices/services': 'microservices-services',
  'microservices/services/new': 'add-microservice',
  'microservices/deployments': 'microservices-deployments',
  'microservices/containers': 'microservices-containers',
  'microservices/workspace': 'microservices-workspace',
  'microservices/map': 'service-mesh',
  'microservices/traces': 'distributed-tracing',
  'microservices/logs': 'microservices-logs',
  'microservices/metrics': 'microservices-metrics',
  'microservices/rca': 'microservices-rca',
  'microservices/dependencies': 'microservices-dependencies',
  'microservices/health': 'microservices-health',
  'microservices/ai': 'microservices-ai',
  'microservices/alerts': 'microservices-alerts',
  'microservices/security': 'microservices-security',
  'microservices/profiling': 'microservices-profiling',
  'microservices/testing': 'microservices-testing',
  'microservices/export': 'microservices-export',
  'ticketing/dashboard': 'ticket-dashboard',
  'ticketing/create': 'create-ticket',
  'ticketing/my-tickets': 'my-tickets',
  'ticketing/team-tickets': 'team-tickets',
  'ticketing/ai-generated': 'ai-tickets',
  'ticketing/automation': 'ticket-automation',
  'ticketing/integrations': 'ticketing-integrations',
  'ticketing/sla': 'sla-management',
  'ticketing/analytics': 'ticket-analytics',
  'incident-commander/dashboard': 'incident-dashboard',
  'incident-commander/war-room': 'war-rooms',
  'incident-commander/predictions': 'incident-predictions',
  'incident-commander/knowledge-graph': 'knowledge-graph',
  'incident-commander/engineers': 'engineer-assignment',
  incidents: 'incident-management',
  'team/members': 'team-members',
  'team/chat': 'team-chat',
  'roles-permissions': 'roles-permissions',
  collaboration: 'collaboration-tools',
  'achievements/badges': 'badges-rewards',
  'system/agents': 'agent-management',
  'system/settings': 'system-settings',
  'system/integrations': 'system-integrations',
  'system/cloud-providers': 'cloud-providers',
  'system/security-events': 'security-compliance',
  'system/notification-center': 'notification-center',
  'system/notification-center/routing-studio': 'routing-studio',
  'system/notification-center/resource-groups': 'resource-groups',
  'system/notification-center/destination-matrix': 'destination-matrix',
  'system/notification-center/communication-hub': 'communication-hub',
  'system/notification-center/ticketing-hub': 'notification-ticketing-hub',
  'system/notification-center/ai-assistant': 'routing-ai-assistant',
  'system/notification-center/war-room': 'integration-war-room',
  'system/notification-center/testing': 'integration-testing-center',
  'system/integrations/slack/overview': 'slack-integration-overview',
  'system/integrations/slack/workspaces': 'slack-workspaces',
  'system/integrations/slack/channels': 'slack-channels',
  'system/integrations/slack/routing-studio': 'slack-routing-studio',
  'system/integrations/slack/alert-rules': 'slack-alert-rules',
  'system/integrations/slack/templates': 'slack-templates',
  'system/integrations/slack/resource-groups': 'slack-resource-groups',
  'system/integrations/slack/logs': 'slack-delivery-logs',
  'system/integrations/slack/analytics': 'slack-analytics',
  'system/integrations/slack/help': 'slack-integration-help',
  'system/integrations/slack/testing': 'slack-integration-testing',
  'payment/billing': 'billing-usage',
  'payment/subscription': 'subscription-plans',
  'payment/invoices': 'invoices-receipts',
  billing: 'billing-usage',
  subscription: 'subscription-plans',
  invoices: 'invoices-receipts',
  support: 'help-center',
  'support/help-center': 'help-center',
  'support/contact': 'contact-support',
  'support/documentation': 'documentation',
  'help-center': 'help-center',
  'contact-support': 'contact-support',
  documentation: 'documentation',
  'time-machine/timeline-explorer': 'timeline-explorer',
  'time-machine/infrastructure-replay': 'infrastructure-replay',
  'time-machine/incident-playback': 'incident-playback',
  'time-machine/system-snapshots': 'system-snapshots',
  'time-machine/state-diff-analyzer': 'state-diff',
};

function stripDynamicSegments(path: string): string {
  if (path.startsWith('monitoring/app-performance/')) return 'monitoring/app-performance';
  if (path.startsWith('monitoring/errors/')) return 'monitoring/errors';
  if (path.startsWith('incident-commander/war-room')) return 'incident-commander/war-room';
  if (path.startsWith('microservices/services/')) return 'microservices/services';
  if (path.startsWith('microservices/projects/')) return 'microservices/projects';
  if (path.startsWith('microservices/traces/')) return 'microservices/traces';
  if (path.startsWith('microservices/containers/')) return 'microservices/containers';
  if (path.startsWith('microservices/clusters/')) return 'microservices/clusters';
  if (path.startsWith('system/integrations/slack/channels/')) return 'system/integrations/slack/channels';
  if (path.startsWith('system/notification-center/resource-groups/')) return 'system/notification-center/resource-groups';
  if (path.startsWith('system/integrations/slack/resource-groups/')) return 'system/integrations/slack/resource-groups';
  if (path.startsWith('system/notification-center/')) return 'system/notification-center';
  return path;
}

export function pathToFeatureId(pathname: string): string {
  const raw = pathname.replace(/^\//, '').replace(/\/$/, '');
  const path = stripDynamicSegments(raw);
  if (ROUTE_TO_FEATURE[path]) return ROUTE_TO_FEATURE[path];
  if (path.startsWith('legal/')) return 'documentation';
  return 'customer-spa-unknown';
}
